"use client";

import { useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Grid,
  Typography,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  Checkbox,
  ListItemText,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip as MuiTooltip,
  Autocomplete,
  TextField,
  CircularProgress,
  Button,
} from "@mui/material";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import EditIcon from "@mui/icons-material/Edit";
import ViewStreamIcon from "@mui/icons-material/ViewStream";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
// old: import { ResponsiveBar } from "@nivo/bar";
import {
  BarChart,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";
import axiosInstance from "../Utils/AxoisClient";
import { toast } from "react-toastify";
import CountryFlag from "react-country-flag";
import countryToFlagCode from "../../Components/CountryFlag/CountryFlag";
import { useLocation } from "react-router-dom";

const getCostComponentColor = (id) => {
  const key = String(id).toUpperCase();
  if (["FOB", "CIF", "EXW"].includes(key)) return "#1E0143";
  return (
    {
      LOGISTICS: "#1021AD",
      "PORT CHARGES": "#592DFF",
      "OCEAN FREIGHT": "#000C8E",
      INSURANCE: "#2D7DFF",
      TAXES: "#52008E",
      TARIFF: "#901FB2",
      "OTHER DUTY": "#5C5CC9",
    }[key] || "#9F91F3"
  );
};



const VOLUME_OPTIMIZER_COLORS = [
  "#1E0143",
  "#3B1E88",
  "#592DFF",
  "#8465FF",
  "#2C003E",
  "#7B2FE0",
  "#A78BFA",
  "#4C1D95",
];

export default function WorkspaceAnalytics() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const ddpDebounceRef = useRef(null);
  const location = useLocation();
  /* -------------------- DYNAMIC DATA -------------------- */

  /* -------------------- STATE -------------------- */
  const [quarter, setQuarter] = useState([]);
  const [country, setCountry] = useState(
    location?.state?.params?.origin_country?.[0] || "",
  );
  // const [selectedIds, setSelectedIds] = useState(
  //   [location?.state?.params?.id] || []
  // );
  const [selectedIds, setSelectedIds] = useState(() =>
    location?.state?.params?.id ? [location.state.params.id] : [],
  );

  const [bookMarkList, setBookMarkList] = useState([]);
  const [ddpPriceData, setDdpPriceData] = useState([]);
  // Same-source totals for the "DDP Price of all Sourcing Countries" table,
  // derived from cost-breakdown-quarter-wise (like the two charts below) so
  // all three stay numerically consistent instead of drifting from the
  // separate ddp-price-data endpoint.
  const [ddpMatrixRows, setDdpMatrixRows] = useState([]);
  const [selectedDDPCountryList, setSelectedDDPCountryList] = useState([]);
  const [selectedQuarter, setSelectedQuarter] = useState({
    id: location?.state?.params?.master_period_id,
    name: location?.state?.params?.period_name,
  });
  const [selectedWorkSpace, setSelectedWorkSpace] = useState(
    [location?.state?.params] || [],
  );
  const [costBreakdown, setCostBreakDown] = useState([]);
  const [costBreakdownQuarterWise, setCostBreakDownQuarterWise] = useState([]);
  const [tableCountries, setTableCountries] = useState([]);
  const [tableDataByCountry, setTableDataByCountry] = useState({});
  const [tableLayout, setTableLayout] = useState("row"); // "row" | "column"

  // Spend Analytics filter chips — UI-only for now (sample data section has
  // no backend yet), but fully interactive dropdowns.
  // Category and Material are objects ({id, name} / the material record)
  // since Material cascades off the selected Category's id, and HS Code is
  // derived from the selected Material rather than its own dropdown.
  const [spendCategory, setSpendCategory] = useState(null);
  const [spendMaterialSel, setSpendMaterialSel] = useState(null);
  const [spendDestination, setSpendDestination] = useState("");
  const [spendDestinationOptions, setSpendDestinationOptions] = useState([]);
  const [spendDestinationLoading, setSpendDestinationLoading] =
    useState(false);
  const [spendPeriod, setSpendPeriod] = useState(null);
  const [spendPeriodOptions, setSpendPeriodOptions] = useState([]);
  const [spendPeriodLoading, setSpendPeriodLoading] = useState(false);
  const [spendSuppliers, setSpendSuppliers] = useState([]);
  const [spendSupplierOptions, setSpendSupplierOptions] = useState([]);
  const [spendSupplierLoading, setSpendSupplierLoading] = useState(false);
  const [spendSupplierLocations, setSpendSupplierLocations] = useState([]);
  const [spendSupplierLocationOptions, setSpendSupplierLocationOptions] =
    useState([]);
  const [spendSupplierLocationLoading, setSpendSupplierLocationLoading] =
    useState(false);

  // Supplier Cost Breakdown chart/table — fetched from the backend once
  // Material, Destination, Suppliers and Supplier Locations are all set.
  const [supplierCostData, setSupplierCostData] = useState([]);
  const [supplierCostLoading, setSupplierCostLoading] = useState(false);
  // Bumped every time the Supplier Cost Breakdown / Volume Optimizer fetch
  // is (re)started, so a slow response for a filter combo the user has
  // since moved on from can't land and overwrite newer, already-cleared
  // state.
  const supplierCostRequestRef = useRef(0);

  // Volume Optimizer — populated directly from the VolumeOptimizer array in
  // the same supplier-graph-data API response the cost breakdown table uses.
  const [volumeOptimizerData, setVolumeOptimizerData] = useState([]);
  // Totals from the API's own `total` object, used in the Volume Optimizer
  // table's TOTAL row instead of re-deriving them client-side.
  const [volumeOptimizerTotals, setVolumeOptimizerTotals] = useState(null);

  // Which row's New Allocation % is currently editable — plain text until
  // the user clicks its edit icon.
  const [editingAllocSupplier, setEditingAllocSupplier] = useState(null);

  // User edits New Allocation % freely (so they can type "23", then "23.3",
  // etc. without being blocked mid-keystroke); New Spend/Savings across all
  // rows only get recalculated once the edited percentages actually sum to
  // 100 — otherwise the user gets an alert and the totals are left as-is
  // until they fix it.
  const handleNewAllocChange = (supplier, rawValue) => {
    const parsed = rawValue === "" ? 0 : Number(rawValue);
    if (Number.isNaN(parsed)) return;
    setVolumeOptimizerData((prev) =>
      prev.map((row) =>
        row.supplier === supplier ? { ...row, newAlloc: parsed } : row,
      ),
    );
  };

  const handleNewAllocBlur = () => {
    setVolumeOptimizerData((prev) => {
      const sum = prev.reduce((s, r) => s + (Number(r.newAlloc) || 0), 0);
      // Allow a small rounding tolerance rather than demanding an exact
      // floating-point 100.
      if (Math.abs(sum - 100) > 0.05) {
        toast.error(
          `New Allocation % must total 100% (currently ${sum.toFixed(1)}%).`,
          { autoClose: 3000 },
        );
        // Stay in edit mode so the user can fix it right away instead of
        // having to click the edit icon again.
        return prev;
      }
      setEditingAllocSupplier(null);
      const totalVolume = prev.reduce((s, r) => s + (r.volume || 0), 0);
      return prev.map((row) => {
        const newSpend =
          ((Number(row.newAlloc) || 0) / 100) * totalVolume * row.ddpPrice;
        return {
          ...row,
          newSpend,
          savings: row.ddpSpend - newSpend,
        };
      });
    });
  };

  const handleClearSpendFilters = () => {
    setSpendCategory(null);
    setSpendMaterialSel(null);
    setSpendDestination("");
    const current = spendPeriodOptions.find((p) => p.is_current);
    setSpendPeriod(current?.id ?? spendPeriodOptions[0]?.id ?? null);
    setSpendSuppliers([]);
    setSpendSupplierLocations([]);
  };

  // Category dropdown — searchable, infinite-scroll category list from the backend.
  const [spendCategoryOptions, setSpendCategoryOptions] = useState([]);
  const [spendCategorySearch, setSpendCategorySearch] = useState("");
  const [spendCategoryPage, setSpendCategoryPage] = useState(1);
  const [spendCategoryHasMore, setSpendCategoryHasMore] = useState(true);
  const [spendCategoryLoading, setSpendCategoryLoading] = useState(false);
  const spendCategoryDebounceRef = useRef(null);
  const spendCategoryAbortRef = useRef(null);
  // Always reads current hasMore/loading at scroll time.
  const spendCategoryScrollBottomRef = useRef(() => { });
  spendCategoryScrollBottomRef.current = () => {
    if (spendCategoryHasMore && !spendCategoryLoading) {
      setSpendCategoryPage((prev) => prev + 1);
    }
  };
  // Stable PaperComponent for the Material dropdown — created once so its
  // identity never changes across renders (a new identity forces MUI to
  // remount the popup and reset its scroll position, which is why the
  // "Loading more" indicator previously caused the list to jump to the top
  // the moment loading toggled true/false). Reads current state via a ref
  // (kept in sync below) rather than direct closure, since the component
  // function itself is only ever created once.
  const spendCategoryStateRef = useRef({ loading: false, hasOptions: false });
  spendCategoryStateRef.current = {
    loading: spendCategoryLoading,
    hasOptions: spendCategoryOptions.length > 0,
  };
  const [SpendCategoryPaper] = useState(() =>
    function SpendCategoryPaper({ children, ...paperProps }) {
      const { loading, hasOptions } = spendCategoryStateRef.current;
      return (
        <Paper
          {...paperProps}
          sx={{
            width: "calc(100% + 24px)",
            ml: "-12px",
            borderRadius: "12px",
            border: "1px solid #eef0f4",
            boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
            overflow: "hidden",
            mt: 0.5,
            "& .MuiAutocomplete-option": {
              fontSize: 13,
              "&.Mui-focused, &.Mui-focusVisible": {
                bgcolor: "#f4f2ff",
              },
              '&[aria-selected="true"]': {
                bgcolor: "#EEE9FF !important",
                color: "#3D1F8C",
                fontWeight: 600,
              },
              '&[aria-selected="true"].Mui-focused': {
                bgcolor: "#E6DFFF !important",
              },
            },
          }}
        >
          {children}
          {loading && hasOptions && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                py: 1,
                borderTop: "1px solid #eef0f4",
              }}
            >
              <CircularProgress size={14} />
              <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                Loading more…
              </Typography>
            </Box>
          )}
        </Paper>
      );
    },
  );

  // Material dropdown — cascades off the selected Category's id; same
  // searchable, infinite-scroll pattern as Category.
  const [spendMaterialOptions, setSpendMaterialOptions] = useState([]);
  const [spendMaterialSearch, setSpendMaterialSearch] = useState("");
  const [spendMaterialPage, setSpendMaterialPage] = useState(1);
  const [spendMaterialHasMore, setSpendMaterialHasMore] = useState(true);
  const [spendMaterialLoading, setSpendMaterialLoading] = useState(false);
  const spendMaterialDebounceRef = useRef(null);
  const spendMaterialAbortRef = useRef(null);
  const spendMaterialScrollBottomRef = useRef(() => { });
  spendMaterialScrollBottomRef.current = () => {
    if (spendMaterialHasMore && !spendMaterialLoading) {
      setSpendMaterialPage((prev) => prev + 1);
    }
  };
  const spendMaterialStateRef = useRef({ loading: false, hasOptions: false });
  spendMaterialStateRef.current = {
    loading: spendMaterialLoading,
    hasOptions: spendMaterialOptions.length > 0,
  };
  const [SpendMaterialPaper] = useState(() =>
    function SpendMaterialPaper({ children, ...paperProps }) {
      const { loading, hasOptions } = spendMaterialStateRef.current;
      return (
        <Paper
          {...paperProps}
          sx={{
            width: "calc(100% + 24px)",
            ml: "-12px",
            borderRadius: "12px",
            border: "1px solid #eef0f4",
            boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
            overflow: "hidden",
            mt: 0.5,
            "& .MuiAutocomplete-option": {
              fontSize: 13,
              "&.Mui-focused, &.Mui-focusVisible": {
                bgcolor: "#f4f2ff",
              },
              '&[aria-selected="true"]': {
                bgcolor: "#EEE9FF !important",
                color: "#3D1F8C",
                fontWeight: 600,
              },
              '&[aria-selected="true"].Mui-focused': {
                bgcolor: "#E6DFFF !important",
              },
            },
          }}
        >
          {children}
          {loading && hasOptions && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                py: 1,
                borderTop: "1px solid #eef0f4",
              }}
            >
              <CircularProgress size={14} />
              <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                Loading more…
              </Typography>
            </Box>
          )}
        </Paper>
      );
    },
  );

  // Spend Analytics / Volume Optimizer sections only render when the
  // backend confirms analytics data exists for this workspace.
  const [showSpendAnalytics, setShowSpendAnalytics] = useState(false);

  const selectedSubCategoryId =
    selectedIds.length > 0
      ? bookMarkList.find((o) => o.id === selectedIds[0])?.subcategory_id
      : null;

  const fetchBookMartList = async (value) => {
    const payload = { search: value ? value : "" };
    try {
      const response = await axiosInstance.post(
        `company/analytics/bookmark-list`,
        payload,
      );
      // setBookMarkList(response?.data?.data);

      const data = response?.data?.data || [];
      setBookMarkList(data);
      // Auto-select first workspace if none selected (no bookmark)
      if (selectedIds?.length < 1 && data?.length > 0) {
        const firstItem = data[0];
        setSelectedIds([firstItem.id]);
        setSelectedWorkSpace([firstItem]);
      }
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
        return;
      }
      toast.error(error?.data?.data || "Failed to fetch data ", {
        autoClose: 2000,
      });
    }
  };

  // const fetchDDPPriceList = async () => {
  //   const payload = {
  //     bookmark_id: selectedIds ? selectedIds?.join(",") : [""], // comma seprated
  //   };
  //   try {
  //     const response = await axiosInstance.post(
  //       `company/analytics/ddp-price-data`,
  //       payload
  //     );
  //     setDdpPriceData(response?.data?.data);
  //     setQuarter(response?.data?.data?.period);
  //     setSelectedDDPCountryList(response?.data?.data?.origin_country);
  //   } catch (error) {
  //     toast.error(error?.response?.data?.message || "Failed to fetch data ", {
  //       autoClose: 2000,
  //     });
  //   }
  // };

  const fetchDDPPriceList = () => {
    // 🔴 clear previous call
    if (ddpDebounceRef.current) {
      clearTimeout(ddpDebounceRef.current);
    }

    // 🟢 debounce new call
    ddpDebounceRef.current = setTimeout(async () => {
      const payload = {
        bookmark_id: selectedIds?.length ? selectedIds.join(",") : "",
      };
      try {
        const response = await axiosInstance.post(
          "company/analytics/ddp-price-data",
          payload,
        );
        const newPeriods = response?.data?.data?.period || [];
        const newCountries = response?.data?.data?.origin_country || [];

        setDdpPriceData(response?.data?.data);
        setQuarter(newPeriods);
        setSelectedDDPCountryList(newCountries);

        // Keep current quarter only if still valid for this workspace, else reset
        setSelectedQuarter((prev) => {
          const stillValid =
            prev?.id && newPeriods.some((p) => p.id === prev.id);
          if (stillValid) return prev;
          return newPeriods.length > 0 ? newPeriods[0] : { id: null, name: null };
        });

        // Keep current country only if still valid for this workspace, else reset
        setCountry((prev) => {
          const stillValid = prev && newCountries.includes(prev);
          if (stillValid) return prev;
          return newCountries.length > 0 ? newCountries[0] : "";
        });
      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to fetch data ", {
          autoClose: 2000,
        });
      }
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (ddpDebounceRef.current) {
        clearTimeout(ddpDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    fetchBookMartList("");
  }, []);

  useEffect(() => {
    if (selectedIds?.length < 1) return;
    fetchDDPPriceList();
  }, [selectedIds]);

  const checkSpendAnalyticsData = async () => {
    try {
      const response = await axiosInstance.get(
        "company/analytics/check-spend-analytics-data",
        { params: { bookmark_id: selectedIds?.join(",") || "" } },
      );
      setShowSpendAnalytics(!!response?.data?.data?.is_analytics_data);
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
        return;
      }
      console.error("check-spend-analytics-data failed:", error);
      setShowSpendAnalytics(false);
    }
  };

  useEffect(() => {
    if (selectedIds?.length < 1) {
      setShowSpendAnalytics(false);
      return;
    }
    checkSpendAnalyticsData();
  }, [selectedIds]);

  const fetchSpendPeriods = async () => {
    setSpendPeriodLoading(true);
    try {
      const response = await axiosInstance.get(
        "company/master-period-list",
      );
      const periods = response?.data?.data || [];
      setSpendPeriodOptions(periods);
      const current = periods.find((p) => p.is_current);
      setSpendPeriod(current?.id ?? periods[0]?.id ?? null);
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
        return;
      }
    } finally {
      setSpendPeriodLoading(false);
    }
  };

  useEffect(() => {
    fetchSpendPeriods();
  }, []);

  const fetchSpendCategories = async (search, page) => {
    if (spendCategoryAbortRef.current) {
      spendCategoryAbortRef.current.abort();
    }
    const controller = new AbortController();
    spendCategoryAbortRef.current = controller;
    setSpendCategoryLoading(true);

    try {
      const response = await axiosInstance.post(
        "company/analytics/spend-analytics-category",
        { category_name: search || "", page, limit: 20 },
        { signal: controller.signal },
      );
      const newItems = response?.data?.data || [];
      setSpendCategoryOptions((prev) =>
        page === 1 ? newItems : [...prev, ...newItems],
      );
      setSpendCategoryHasMore(!!response?.data?.pagination?.hasNextPage);
      if (page === 1 && !search && newItems.length > 0) {
        setSpendCategory((prev) => prev ?? newItems[0]);
      }
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
        return;
      }
    } finally {
      setSpendCategoryLoading(false);
    }
  };

  // Load the first page of Categories on mount so a default selection can
  // be made without the user having to open the dropdown first.
  useEffect(() => {
    fetchSpendCategories("", 1);
  }, []);

  // Re-search from page 1 whenever the search text changes (debounced).
  // Skipped on mount — this should only fire once the user actually types,
  // not 400ms after mount regardless of what happened in between (which
  // was resetting an already-scrolled/appended list back to page 1).
  const spendCategorySearchMounted = useRef(false);
  useEffect(() => {
    if (!spendCategorySearchMounted.current) {
      spendCategorySearchMounted.current = true;
      return;
    }
    if (spendCategoryDebounceRef.current) {
      clearTimeout(spendCategoryDebounceRef.current);
    }
    spendCategoryDebounceRef.current = setTimeout(() => {
      setSpendCategoryPage(1);
      fetchSpendCategories(spendCategorySearch, 1);
    }, 400);
    return () => clearTimeout(spendCategoryDebounceRef.current);
  }, [spendCategorySearch]);

  // Load the next page when the dropdown list is scrolled to the bottom.
  useEffect(() => {
    if (spendCategoryPage === 1) return;
    fetchSpendCategories(spendCategorySearch, spendCategoryPage);
  }, [spendCategoryPage]);

  const fetchSpendMaterials = async (categoryId, search, page) => {
    if (!categoryId) return;
    if (spendMaterialAbortRef.current) {
      spendMaterialAbortRef.current.abort();
    }
    const controller = new AbortController();
    spendMaterialAbortRef.current = controller;
    setSpendMaterialLoading(true);

    try {
      const response = await axiosInstance.post(
        "company/analytics/spend-analytics-material",
        { category_id: categoryId, material_name: search || "", page, limit: 20 },
        { signal: controller.signal },
      );
      const newItems = response?.data?.data || [];
      setSpendMaterialOptions((prev) =>
        page === 1 ? newItems : [...prev, ...newItems],
      );
      setSpendMaterialHasMore(!!response?.data?.pagination?.hasNextPage);
      if (page === 1 && !search && newItems.length > 0) {
        setSpendMaterialSel((prev) => prev ?? newItems[0]);
      }
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
        return;
      }
    } finally {
      setSpendMaterialLoading(false);
    }
  };

  // Re-search Materials from page 1 when the search text changes (debounced,
  // skipped on mount for the same reason as Category above).
  const spendMaterialSearchMounted = useRef(false);
  useEffect(() => {
    if (!spendMaterialSearchMounted.current) {
      spendMaterialSearchMounted.current = true;
      return;
    }
    if (spendMaterialDebounceRef.current) {
      clearTimeout(spendMaterialDebounceRef.current);
    }
    spendMaterialDebounceRef.current = setTimeout(() => {
      setSpendMaterialPage(1);
      fetchSpendMaterials(spendCategory?.id, spendMaterialSearch, 1);
    }, 400);
    return () => clearTimeout(spendMaterialDebounceRef.current);
  }, [spendMaterialSearch]);

  // Load the next page of Materials when scrolled to the bottom.
  useEffect(() => {
    if (spendMaterialPage === 1) return;
    fetchSpendMaterials(spendCategory?.id, spendMaterialSearch, spendMaterialPage);
  }, [spendMaterialPage]);

  // Whenever the selected Category changes, reset Material (it belongs to
  // the old category and no longer applies), clear the cached options, and
  // fetch fresh options for the new category so a default selection can be
  // made without the user having to open the dropdown first.
  useEffect(() => {
    setSpendMaterialSel(null);
    setSpendMaterialOptions([]);
    setSpendMaterialSearch("");
    setSpendMaterialPage(1);
    setSpendMaterialHasMore(true);
    if (spendCategory?.id) {
      fetchSpendMaterials(spendCategory.id, "", 1);
    }
  }, [spendCategory?.id]);

  const fetchSpendDestinations = async (materialId) => {
    if (!materialId) return;
    setSpendDestinationLoading(true);
    try {
      const response = await axiosInstance.post(
        "company/analytics/spend-analytics-material-destination",
        { material_id: materialId },
      );
      const newItems = (response?.data?.data || []).map(
        (item) => item?.plant_location_destination,
      );
      setSpendDestinationOptions(newItems);
      if (newItems.length > 0) {
        setSpendDestination((prev) => prev || newItems[0]);
      }
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
        return;
      }
    } finally {
      setSpendDestinationLoading(false);
    }
  };

  // Destination depends on the selected Material — reset it and re-fetch
  // fresh options whenever the Material changes.
  useEffect(() => {
    setSpendDestination("");
    setSpendDestinationOptions([]);
    if (spendMaterialSel?.id) {
      fetchSpendDestinations(spendMaterialSel.id);
    }
  }, [spendMaterialSel?.id]);

  const fetchSpendSuppliers = async (materialId, destination) => {
    if (!materialId || !destination) return;
    setSpendSupplierLoading(true);
    try {
      const response = await axiosInstance.post(
        "company/analytics/spend-analytics-suppliers",
        {
          material_id: materialId,
          plant_location_destination: destination,
        },
      );
      const newItems = (response?.data?.data || []).map(
        (item) => item?.supplier_name,
      );
      setSpendSupplierOptions(newItems);
      if (newItems.length > 0) {
        setSpendSuppliers((prev) => (prev.length ? prev : newItems));
      }
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
        return;
      }
    } finally {
      setSpendSupplierLoading(false);
    }
  };

  // Suppliers depends on Material + Destination — reset and re-fetch
  // whenever either of those change.
  useEffect(() => {
    setSpendSuppliers([]);
    setSpendSupplierOptions([]);
    if (spendMaterialSel?.id && spendDestination) {
      fetchSpendSuppliers(spendMaterialSel.id, spendDestination);
    }
  }, [spendMaterialSel?.id, spendDestination]);

  // Supplier Location is keyed on a single supplier per call, so with
  // multi-select Suppliers we fetch locations for every selected supplier
  // and merge the deduped results into one options list.
  const fetchSpendSupplierLocations = async (
    materialId,
    destination,
    supplierNames,
  ) => {
    if (!materialId || !destination || !supplierNames?.length) return;
    setSpendSupplierLocationLoading(true);
    try {
      const responses = await Promise.all(
        supplierNames.map((supplierName) =>
          axiosInstance.post(
            "company/analytics/spend-analytics-supplier-location",
            {
              material_id: materialId,
              plant_location_destination: destination,
              supplier_name: supplierName,
            },
          ),
        ),
      );
      const merged = responses.flatMap(
        (response) =>
          (response?.data?.data || []).map(
            (item) => item?.supplier_origin_country,
          ),
      );
      const newItems = Array.from(new Set(merged.filter(Boolean)));
      setSpendSupplierLocationOptions(newItems);
      if (newItems.length > 0) {
        setSpendSupplierLocations((prev) =>
          prev.length ? prev : newItems,
        );
      }
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
        return;
      }
    } finally {
      setSpendSupplierLocationLoading(false);
    }
  };

  // Supplier Location depends on Material + Destination + Suppliers — reset
  // and re-fetch whenever any of those change.
  useEffect(() => {
    setSpendSupplierLocations([]);
    setSpendSupplierLocationOptions([]);
    if (spendMaterialSel?.id && spendDestination && spendSuppliers.length) {
      fetchSpendSupplierLocations(
        spendMaterialSel.id,
        spendDestination,
        spendSuppliers,
      );
    }
  }, [spendMaterialSel?.id, spendDestination, spendSuppliers]);

  const fetchSupplierCostBreakdown = async (
    materialId,
    destination,
    supplierNames,
    supplierLocations,
    periodId,
  ) => {
    const requestId = ++supplierCostRequestRef.current;
    setSupplierCostLoading(true);
    try {
      const response = await axiosInstance.post(
        "company/analytics/spend-analytics-supplier-graph-data",
        {
          material_id: materialId,
          plant_location_destination: destination,
          supplier_name: supplierNames,
          supplier_origin_country: supplierLocations,
          period_id: periodId || null,
        },
      );
      if (requestId !== supplierCostRequestRef.current) return;
      const rows = response?.data?.data?.SupplierCostBreakdown || [];
      const orNa = (v) => (v === "" || v == null ? "-" : v);
      // Combines an origin-side and destination-side value into one figure
      // (e.g. origin + destination terminal handling charges) — "-" only
      // when both sides are missing, otherwise sums whichever are present.
      const sumSides = (a, b) => {
        const aNum = a === "" || a == null ? null : Number(a);
        const bNum = b === "" || b == null ? null : Number(b);
        if (aNum == null && bNum == null) return "-";
        return (aNum || 0) + (bNum || 0);
      };
      const mapped = rows.map((row) => {
        const origin = row.origin_cost_data || {};
        const oceanFreight = row.ocean_freight_data || {};
        const destination = row.destination_cost_data || {};
        const vat = row.vat_data || {};
        // Port Charges = origin + destination customs clearance + terminal
        // handling, all folded into one figure to match the chart legend.
        const customsClearance = sumSides(
          origin.origin_customs_clearance,
          destination.destination_customs_clearance,
        );
        const terminalHandling = sumSides(
          origin.origin_terminal_handling_charges,
          destination.destination_terminal_handling_charges,
        );
        const portCharges =
          customsClearance === "-" && terminalHandling === "-"
            ? "-"
            : (customsClearance === "-" ? 0 : customsClearance) +
            (terminalHandling === "-" ? 0 : terminalHandling);
        return {
          supplier: row.supplier,
          location: row.supplier_location,
          volume: Number(row.volume) || 0,
          currentSpend: Number(row.current_spend) || 0,
          incoterm: row.quoted_incoterm,
          quotedPrice: Number(row.quoted) || 0,
          exw: row.exw === "" || row.exw == null ? 0 : Number(row.exw),
          ddp: Number(row.DDP ?? row.ddu) || 0,
          taxes: orNa(vat.vat_value),
          otherDuties: orNa(destination.other_charge_calculated_value),
          tariff: orNa(destination.tariff_value),
          logistics: sumSides(origin.logistics_cost, destination.logistics_cost),
          portCharges,
          // Insurance = 1% of FOB, computed client-side rather than taken
          // from ocean_freight_data.insurance.
          insurance:
            row.fob === "" || row.fob == null ? "-" : Number(row.fob) * 0.01,
          oceanFreight: orNa(oceanFreight.ocean_freight_road_freight),
        };
      });
      setSupplierCostData(mapped);

      // Supplier Location and DDP Price aren't on the VolumeOptimizer rows
      // themselves — join them in from the cost breakdown rows by supplier.
      const bySupplier = Object.fromEntries(mapped.map((r) => [r.supplier, r]));
      const volumeRows = response?.data?.data?.VolumeOptimizer || [];
      const mappedVolume = volumeRows.map((row, i) => {
        const costRow = bySupplier[row.supplier];
        const ddpPrice = costRow?.ddp || 0;
        const volume = Number(row.volume) || 0;
        return {
          supplier: row.supplier,
          location: costRow?.location,
          incoterm: row.quoted_incoterm,
          quotedPrice: Number(row.quoted) || 0,
          ddpPrice,
          volume,
          currentAlloc: Number(row.alloc_percentage) || 0,
          newAlloc: Number(row.new_alloc_percentage) || 0,
          currentSpend: Number(row.current_spend) || 0,
          ddpSpend: ddpPrice * volume,
          newSpend: Number(row.new_spend) || 0,
          savings: Number(row.saving) || 0,
          bold: i === 0,
        };
      });
      setVolumeOptimizerData(mappedVolume);

      const apiTotal = response?.data?.data?.total;
      setVolumeOptimizerTotals(
        apiTotal
          ? {
              volume: Number(apiTotal.volume_sum) || 0,
              currentSpend: Number(apiTotal.current_spend_sum) || 0,
              currentAllocPct:
                Number(apiTotal.current_allocation_sum_percentage) || 0,
              newAllocPct: Number(apiTotal.new_allocation_sum_percentage) || 0,
              newSpend: Number(apiTotal.new_allocation_sum) || 0,
              savings: Number(apiTotal.saving_sum) || 0,
            }
          : null,
      );
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
        return;
      }
      if (requestId !== supplierCostRequestRef.current) return;
      setSupplierCostData([]);
      setVolumeOptimizerData([]);
      setVolumeOptimizerTotals(null);
    } finally {
      if (requestId === supplierCostRequestRef.current) {
        setSupplierCostLoading(false);
      }
    }
  };

  // The Supplier Cost Breakdown chart/table depends on every filter chip
  // except Category (Material already implies it) — re-fetch whenever the
  // full set required by the API is present. Clear the previous result
  // immediately (not just on error) so a filter change never leaves stale
  // data from the old selection on screen while the new fetch is in flight.
  useEffect(() => {
    setSupplierCostData([]);
    setVolumeOptimizerData([]);
    setVolumeOptimizerTotals(null);
    if (
      spendMaterialSel?.id &&
      spendDestination &&
      spendSuppliers.length &&
      spendSupplierLocations.length
    ) {
      fetchSupplierCostBreakdown(
        spendMaterialSel.id,
        spendDestination,
        spendSuppliers,
        spendSupplierLocations,
        spendPeriod,
      );
    }
  }, [
    spendMaterialSel?.id,
    spendDestination,
    spendSuppliers,
    spendSupplierLocations,
    spendPeriod,
  ]);

  const toNumber = (v) => {
    if (v === null || v === undefined || v === "") return 0;
    const n = Number(v);
    return Number.isFinite(n) ? Number(n?.toFixed(1)) : 0;
  };

  // Parses without rounding — use inside sums so each addend keeps full
  // precision; round the sum itself once, at the end, with toNumber.
  const toRaw = (v) => {
    if (v === null || v === undefined || v === "") return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  // Display formatter for the Supplier Cost Breakdown / Volume Optimizer
  // tables — always exactly 2 decimal places, with thousands grouping.
  // Passes "-" placeholders through unchanged instead of coercing to "0.00".
  const fmt2 = (v) => {
    if (v === "-" || v === null || v === undefined) return "-";
    const n = Number(v);
    if (!Number.isFinite(n)) return "-";
    return n.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getCostBreakDownList = async () => {
    const payload = {
      bookmark_id: selectedIds ? selectedIds?.join(",") : [""],
      quarter_id: selectedQuarter?.id,
    };
    try {
      const response = await axiosInstance.post(
        `company/analytics/cost-breakdown-value`,
        payload,
      );
      const data = response?.data?.data?.breakdown;
      const getKeyAndValue = (kv = {}) => {
        if (!kv || typeof kv !== "object") return { key: "NA", value: 0 };

        if (kv.EXW !== "" && kv.EXW !== null && kv.EXW !== undefined)
          return { key: "EXW", value: kv.EXW };

        if (kv.FOB !== "" && kv.FOB !== null && kv.FOB !== undefined)
          return { key: "FOB", value: kv.FOB };

        if (kv.CIF !== "" && kv.CIF !== null && kv.CIF !== undefined)
          return { key: "CIF", value: kv.CIF };

        return { key: "NA", value: 0 };
      };
      const result = Object.entries(data)?.map(([country, value]) => {
        const { key, value: finalValue } = getKeyAndValue(value?.key_value);
        return {
          country:
            country !== "USA"
              ? country.charAt(0).toUpperCase() + country.slice(1).toLowerCase()
              : country,
          [key]: toNumber(finalValue),
          // EXW: toNumber(value.key_value?.EXW),
          Logistics: toNumber(
            toRaw(value.origin_cost_data?.logistics_cost) +
            toRaw(value.destination_cost_data?.logistics_cost),
          ),

          "Port Charges": toNumber(
            toRaw(value.origin_cost_data?.origin_customs_clearance) +
            toRaw(
              value.destination_cost_data?.destination_customs_clearance,
            ) +
            toRaw(
              value.destination_cost_data
                ?.destination_terminal_handling_charges,
            ) +
            toRaw(
              value.origin_cost_data?.origin_terminal_handling_charges,
            ),
          ),

          "Ocean Freight": toNumber(
            value.ocean_freight_data?.ocean_freight_road_freight,
          ),
          // Insurance = 1% of FOB — falls back to whichever base price
          // (EXW/FOB/CIF) this row actually has when FOB itself isn't
          // returned, so it's never silently 0 or stuck on a stale value.
          Insurance: toNumber(
            toRaw(value?.key_value?.FOB || finalValue) * 0.01,
          ),
          Taxes: toNumber(
            toRaw(value.origin_cost_data?.tax) +
            toRaw(value.origin_cost_data?.export_duty) +
            toRaw(value?.vat_data?.vatCalculated) +
            toRaw(value.origin_cost_data?.additional_charges),
          ),

          Tariff: toNumber(value.destination_cost_data?.tariff),

          "Other Duty": toNumber(
            value.destination_cost_data?.other_charge_calculated_value,
          ),
        };
      });

      setCostBreakDown(result);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to fetch data ", {
        autoClose: 2000,
      });
    }
  };

  useEffect(() => {
    if (!selectedQuarter?.name || selectedIds.length < 1) return;
    getCostBreakDownList();
  }, [selectedQuarter, selectedIds]);

  const transformQuarterWiseData = (apiData = []) => {
    if (!Array.isArray(apiData)) return [];

    const getKeyAndValue = (kv = {}) => {
      if (!kv || typeof kv !== "object") return { key: "NA", value: 0 };

      if (kv.EXW !== "" && kv.EXW !== null && kv.EXW !== undefined)
        return { key: "EXW", value: kv.EXW };

      if (kv.FOB !== "" && kv.FOB !== null && kv.FOB !== undefined)
        return { key: "FOB", value: kv.FOB };

      if (kv.CIF !== "" && kv.CIF !== null && kv.CIF !== undefined)
        return { key: "CIF", value: kv.CIF };

      return { key: "NA", value: 0 };
    };
    return apiData.flatMap((item) =>
      (item?.data || []).map((row) => {
        const raw = row?.raw_data || {};
        const { key, value: finalValue } = getKeyAndValue(raw?.key_value);
        return {
          quarter: row?.quarter || "",
          // EXW: toNumber(raw.key_value?.EXW),
          [key]: toNumber(finalValue),
          Logistics: toNumber(
            toRaw(raw.origin_cost_data?.logistics_cost) +
            toRaw(raw.destination_cost_data?.logistics_cost),
          ),

          "Port Charges": toNumber(
            toRaw(raw.origin_cost_data?.origin_customs_clearance) +
            toRaw(
              raw.destination_cost_data?.destination_customs_clearance,
            ) +
            toRaw(
              raw.destination_cost_data
                ?.destination_terminal_handling_charges,
            ) +
            toRaw(raw.origin_cost_data?.origin_terminal_handling_charges),
          ),

          "Ocean Freight": toNumber(
            raw.ocean_freight_data?.ocean_freight_road_freight,
          ),
          // Insurance = 1% of FOB — falls back to whichever base price
          // (EXW/FOB/CIF) this row actually has when FOB itself isn't
          // returned, so it's never silently 0 or stuck on a stale value.
          Insurance: toNumber(toRaw(raw?.key_value?.FOB || finalValue) * 0.01),
          Taxes: toNumber(
            toRaw(raw.origin_cost_data?.tax) +
            toRaw(raw.origin_cost_data?.export_duty) +
            toRaw(raw?.vat_data?.vatCalculated) +
            toRaw(raw.origin_cost_data?.additional_charges),
          ),

          Tariff: toNumber(raw.destination_cost_data?.tariff),

          "Other Duty": toNumber(
            raw.destination_cost_data?.other_charge_calculated_value,
          ),
        };
      }),
    );

    // return apiData.map((item) => {
    //   const raw = item?.data || {};

    //   return {
    //     quarter: raw[0]?.quarter,

    //     EXW: toNumber(raw?.key_value?.EXW),

    //     Logistics:
    //       toNumber(raw?.origin_cost_data?.logistics_cost) +
    //       toNumber(raw?.origin_cost_data?.origin_customs_clearance) +
    //       toNumber(raw?.origin_cost_data?.origin_terminal_handling_charges) +
    //       toNumber(raw?.origin_cost_data?.export_duty) +
    //       toNumber(raw?.origin_cost_data?.tax),

    //     OceanFreight:
    //       toNumber(raw?.ocean_freight_data?.ocean_freight_road_freight) +
    //       toNumber(raw?.ocean_freight_data?.insurance),

    //     Tariff: toNumber(raw?.destination_cost_data?.tariff),

    //     Others: toNumber(raw?.destination_cost_data?.additional_charges),
    //   };
    // });
  };

  const getCostBreakdownQuarterWise = async () => {
    const payload = {
      bookmark_id: selectedIds?.join(",") || "",
      origin_country: [country].filter(Boolean).join(","),
    };

    try {
      const response = await axiosInstance.post(
        "company/analytics/cost-breakdown-quarter-wise",
        payload,
      );
      const apiResponse = response?.data?.data || [];
      const chartData = transformQuarterWiseData(apiResponse);
      setCostBreakDownQuarterWise(chartData);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to fetch data ", {
        autoClose: 2000,
      });
    }
  };

  useEffect(() => {
    if (!selectedDDPCountryList.length || selectedIds.length < 1 || !country)
      return;
    getCostBreakdownQuarterWise();
  }, [selectedDDPCountryList, country, selectedIds]);

  useEffect(() => {
    setTableCountries((prev) => {
      const valid = prev.filter((c) => selectedDDPCountryList?.includes(c));
      if (valid.length) return valid;
      return selectedDDPCountryList?.length
        ? selectedDDPCountryList.slice(0, 2)
        : [];
    });
  }, [selectedDDPCountryList]);

  const getCostBreakdownQuarterWiseForCountry = async (targetCountry) => {
    const payload = {
      bookmark_id: selectedIds?.join(",") || "",
      origin_country: targetCountry,
    };

    try {
      const response = await axiosInstance.post(
        "company/analytics/cost-breakdown-quarter-wise",
        payload,
      );
      const apiResponse = response?.data?.data || [];
      return transformQuarterWiseData(apiResponse);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to fetch data ", {
        autoClose: 2000,
      });
      return [];
    }
  };

  useEffect(() => {
    if (!tableCountries.length || selectedIds.length < 1) return;
    let cancelled = false;

    (async () => {
      const entries = await Promise.all(
        tableCountries.map(async (c) => [
          c,
          await getCostBreakdownQuarterWiseForCountry(c),
        ]),
      );
      if (!cancelled) {
        setTableDataByCountry(Object.fromEntries(entries));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tableCountries, selectedIds]);

  const rowTotal = (row) =>
    toNumber(
      Object.entries(row || {}).reduce(
        (sum, [k, v]) => (k === "quarter" ? sum : sum + toRaw(v)),
        0,
      ),
    );

  // Builds the "DDP Price of all Sourcing Countries" matrix from the same
  // per-country quarter-wise data the two charts below use, so all three
  // numbers always agree.
  useEffect(() => {
    if (!selectedDDPCountryList?.length || selectedIds.length < 1) {
      setDdpMatrixRows([]);
      return;
    }
    let cancelled = false;

    (async () => {
      const perCountry = await Promise.all(
        selectedDDPCountryList.map(async (c) => [
          c,
          await getCostBreakdownQuarterWiseForCountry(c),
        ]),
      );
      if (cancelled) return;

      const quarterMap = {};
      perCountry.forEach(([country, rows]) => {
        rows.forEach((row) => {
          if (!row?.quarter) return;
          if (!quarterMap[row.quarter]) {
            quarterMap[row.quarter] = { quarter: row.quarter };
          }
          quarterMap[row.quarter][country] = rowTotal(row);
        });
      });
      setDdpMatrixRows(Object.values(quarterMap));
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedDDPCountryList, selectedIds]);

  const AxisTickWithFlag = ({ x, y, value }) => {
    const countryCode = countryToFlagCode[value];
    if (!countryCode) return null;

    const flagUrl = `https://flagcdn.com/w20/${countryCode.toLowerCase()}.png`;

    const FLAG_W = 18;
    const FLAG_H = 1000;
    const GAP = 6;
    const BAR_GAP = 10;

    return (
      // <g transform={`translate(${x - (FLAG_W + GAP + BAR_GAP)}, ${y})`}>
      <g transform={`translate(${x - 80}, ${y})`}>
        {/* Flag */}
        <image
          href={flagUrl}
          x={0}
          y={-FLAG_H / 2}
          width={FLAG_W}
          height={FLAG_H}
          preserveAspectRatio="xMidYMid meet"
        />

        {/* Label */}
        <text
          x={FLAG_W + GAP}
          y={0}
          dominantBaseline="middle"
          fontSize={12}
          fill="#475569"
        >
          {value}
        </text>
      </g>
    );
  };

  /* -------------------- UI -------------------- */
  return (
    <Box sx={{ p: 2, background: "#f7f8fc", minHeight: "100vh" }}>
      <Paper sx={{ p: 2, borderRadius: 2 }}>
        {/* Header */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Select
              fullWidth
              size="small"
              multiple
              displayEmpty
              value={selectedIds}
              onChange={(e) => {
                setSelectedIds(e.target.value);
                const selectedObjects = bookMarkList.filter((item) =>
                  Array.isArray(e.target.value)
                    ? e.target.value.includes(item.id)
                    : item.id === e.target.value,
                );
                setSelectedWorkSpace(selectedObjects);
              }}
              renderValue={(selected) => {
                if (selected.length === 0) return "Select Workspace";
                return bookMarkList
                  ?.filter((o) => selected?.includes(o.id))
                  ?.map((o) => o.bookmark_title)
                  .join(", ");
              }}
            >
              <MenuItem disabled value="">
                Select Workspace
              </MenuItem>

              {bookMarkList?.map((item) => {
                const isSelected = selectedIds.includes(item.id);
                const disable =
                  selectedSubCategoryId !== null &&
                  item.subcategory_id !== selectedSubCategoryId &&
                  !isSelected;

                return (
                  <MenuItem key={item.id} value={item.id} disabled={disable}>
                    <Checkbox checked={isSelected} />
                    <ListItemText
                      primary={item.bookmark_title}
                      secondary={`${item.subcategory_name} (${item.destination_country}) (${(item?.period_name && item?.period_name) || ""})`}
                    />
                  </MenuItem>
                );
              })}
            </Select>
          </Grid>

          {Array.isArray(selectedWorkSpace) &&
            selectedWorkSpace[0]?.subcategory_name && (
              <Grid item xs={12} md={6}>
                <Typography variant="caption" sx={cardHeader}>
                  Category
                </Typography>
                <Paper sx={infoCard}>
                  <Typography fontWeight={500}>
                    {/* {data.category} */}
                    {Array.isArray(selectedWorkSpace)
                      ? selectedWorkSpace[0]?.subcategory_name
                      : ""}
                  </Typography>
                </Paper>
              </Grid>
            )}
          <Grid item xs={12} md={6}>
            <Typography variant="caption" sx={cardHeader}>
              Destination Country
            </Typography>
            <Paper sx={infoCard}>
              <Typography fontWeight={500}>
                {/* {data.destination} */}
                {Array.isArray(selectedWorkSpace)
                  ? selectedWorkSpace[0]?.destination_country
                  : ""}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
        {/* Top Chart */}
        <Box sx={{ mt: 3 }}>
          <Typography
            fontSize={14}
            mb={1}
            fontWeight={600}
            sx={{ display: "flex", justifyContent: "center" }}
          >
            DDP Price of all Sourcing Countries
          </Typography>

          {ddpMatrixRows.length > 0 &&
            (() => {
              const quarterSortValue = (label = "") => {
                const match = String(label).match(/Q(\d)\D*(\d{4})/i);
                if (!match) return 0;
                const [, q, year] = match;
                return Number(year) * 10 + Number(q);
              };
              const quarters = ddpMatrixRows
                .map((d) => d.quarter)
                .sort((a, b) => quarterSortValue(a) - quarterSortValue(b));
              const countries = Object.keys(ddpMatrixRows[0]).filter(
                (key) => key !== "quarter",
              );
              return (
                <Box sx={{ overflowX: "auto" }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: `minmax(140px, 1fr) ${quarters.map(() => "1fr").join(" ")}`,
                      gap: "8px",
                      minWidth: quarters.length > 4 ? 600 : "auto",
                      p: 1,
                    }}
                  >
                    {/* Header row */}
                    <Box
                      sx={{
                        bgcolor: "#1e0143",
                        // bgcolor: "#1021ad",
                        color: "#fff",
                        borderRadius: "8px",
                        p: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 600,
                        fontSize: 13,
                      }}
                    >
                      Origin Country
                    </Box>
                    {quarters.map((q) => (
                      <Box
                        key={q}
                        sx={{
                          // bgcolor: "#1976d2",
                          // bgcolor: "#1021ad",
                          bgcolor: "#1e0143",
                          color: "white",
                          borderRadius: "8px",
                          p: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 600,
                          fontSize: 13,
                        }}
                      >
                        {q}
                      </Box>
                    ))}

                    {/* Data rows */}
                    {countries.map((country) => (
                      <>
                        <Box
                          key={`label-${country}`}
                          sx={{
                            bgcolor: "#1e0143",
                            // bgcolor: "#1021ad",

                            color: "white",
                            borderRadius: "8px",
                            p: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 600,
                            fontSize: 12,
                          }}
                        >
                          {country}
                        </Box>
                        {quarters.map((q) => {
                          const row = ddpMatrixRows.find(
                            (d) => d.quarter === q,
                          );
                          const val = row?.[country];
                          return (
                            <Box
                              key={`${country}-${q}`}
                              sx={{
                                // bgcolor: "#1e0143",
                                bgcolor: "#fff",
                                color: "#1e0143",
                                border: "1px solid #1e0143",
                                borderRadius: "8px",
                                p: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: 700,
                                fontSize: 12,
                              }}
                            >
                              {val !== null && val !== undefined && val !== ""
                                ? Number(val).toFixed(1)
                                : "-"}
                            </Box>
                          );
                        })}
                      </>
                    ))}
                  </Box>
                </Box>
              );
            })()}
        </Box>

        {/* Bottom Section */}
        {/* old: no alignItems stretch */}
        <Grid container spacing={2} mt={2} alignItems="stretch">
          {/* Work Space*/}
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            {/* old: margin: "0px 8px 16px 8px" */}
            <Paper
              sx={{
                ...chartCard,
                flex: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Select
                fullWidth
                size="small"
                sx={{ mb: 1 }}
                // value={selectedQuarter || ""}
                value={selectedQuarter?.id || ""}
                displayEmpty
                onChange={(e) => {
                  const id = e.target.value;
                  const fullObject = quarter.find((q) => q.id === id);
                  setSelectedQuarter(fullObject);
                }}
              >
                {quarter?.length > 0 &&
                  quarter?.map((q) => (
                    <MenuItem key={q.id} value={q.id}>
                      {q?.name}
                    </MenuItem>
                  ))}
              </Select>

              <Typography fontSize={13} mb={1} mt={2} sx={centerDiv}>
                Cost Breakdown (USD per MT)
              </Typography>

              {/* old: ResponsiveBar horizontal stacked from @nivo/bar for Cost Breakdown */}
              <Box sx={{ flex: 1, minHeight: { xs: 250, md: 200 } }}>
                {(() => {
                  const cbData = Array.isArray(costBreakdown)
                    ? costBreakdown
                    : [];
                  if (cbData.length === 0) {
                    return (
                      <Box
                        sx={{
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#666",
                          fontSize: 14,
                        }}
                      >
                        No Data Found
                      </Box>
                    );
                  }
                  const sortOrder = [
                    "EXW",
                    "FOB",
                    "CIF",
                    "LOGISTICS",
                    "PORT CHARGES",
                    "OCEAN FREIGHT",
                    "INSURANCE",
                    "TAXES",
                    "TARIFF",
                    "OTHER DUTY",
                  ];
                  const cbKeys = cbData.length
                    ? Array.from(
                      new Set(
                        cbData.flatMap((o) =>
                          Object.keys(o).filter((k) => k !== "country"),
                        ),
                      ),
                    ).sort(
                      (a, b) =>
                        sortOrder.indexOf(a.toUpperCase()) -
                        sortOrder.indexOf(b.toUpperCase()),
                    )
                    : [];
                  const getColor = (id) => {
                    const key = String(id).toUpperCase();
                    if (["FOB", "CIF", "EXW"].includes(key)) return "#1E0143";
                    return (
                      {
                        LOGISTICS: "#1021AD",
                        "PORT CHARGES": "#592DFF",
                        "OCEAN FREIGHT": "#000C8E",
                        INSURANCE: "#2D7DFF",
                        TAXES: "#52008E",
                        TARIFF: "#901FB2",
                        OTHERS: "#5C5CC9",
                      }[key] || "#9F91F3"
                    );
                  };
                  const legendKeys =
                    cbData.length > 0
                      ? [
                        "Input Price",
                        ...Array.from(
                          new Set(
                            cbData.flatMap((row) =>
                              Object.keys(row).filter((k) => k !== "country"),
                            ),
                          ),
                        ).filter(
                          (k) =>
                            !["EXW", "FOB", "CIF"].includes(k.toUpperCase()),
                        ),
                      ]
                      : [];
                  return (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={cbData}
                        layout="vertical"
                        stackOffset="none"
                        margin={{
                          top: 10,
                          right: 10,
                          bottom: 5,
                          left: isMobile ? 0 : 5,
                        }}
                        barCategoryGap="20%"
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          horizontal={false}
                          stroke="#f0f0f0"
                        />
                        <XAxis
                          type="number"
                          tickLine={false}
                          tick={{ fontSize: isMobile ? 10 : 12 }}
                        />
                        <YAxis
                          type="category"
                          dataKey="country"
                          tickLine={false}
                          tick={({ x, y, payload }) => {
                            const code = countryToFlagCode[payload.value];
                            return (
                              <g transform={`translate(${x},${y})`}>
                                {code && (
                                  <image
                                    href={`https://flagcdn.com/w20/${code.toLowerCase()}.png`}
                                    x={-70}
                                    y={-8}
                                    width={16}
                                    height={16}
                                    preserveAspectRatio="xMidYMid meet"
                                  />
                                )}
                                <text
                                  x={-50}
                                  y={0}
                                  dominantBaseline="middle"
                                  fontSize={11}
                                  fill="#475569"
                                >
                                  {payload.value}
                                </text>
                              </g>
                            );
                          }}
                          width={isMobile ? 70 : 80}
                        />
                        <Tooltip
                          formatter={(v) => Number(v).toFixed(1)}
                          contentStyle={{
                            fontSize: 11,
                            borderRadius: 4,
                            padding: "4px 8px",
                          }}
                          wrapperStyle={{ zIndex: 1000, pointerEvents: "none" }}
                          cursor={{ fill: "transparent" }}
                          position={{ x: undefined, y: 0 }}
                        />
                        <Legend
                          verticalAlign="bottom"
                          align="center"
                          wrapperStyle={{
                            paddingTop: 5,
                            fontSize: isMobile ? 9 : 11,
                            lineHeight: "18px",
                          }}
                          iconType="square"
                          iconSize={10}
                          payload={legendKeys.map((k) => ({
                            value: k,
                            type: "square",
                            color: getColor(k === "Input Price" ? "EXW" : k),
                          }))}
                        />
                        {cbKeys.map((key, i) => (
                          <Bar
                            key={key}
                            dataKey={key}
                            stackId="stack"
                            fill={getColor(key)}
                          >
                            {i === cbKeys.length - 1 && (
                              <LabelList
                                dataKey={key}
                                position="right"
                                content={({ x, y, width, height, index }) => {
                                  const row = cbData[index];
                                  const total = cbKeys.reduce(
                                    (sum, k) => sum + (Number(row?.[k]) || 0),
                                    0,
                                  );
                                  return (
                                    <text
                                      x={x + width + 6}
                                      y={y + height / 2}
                                      dominantBaseline="middle"
                                      fontSize={11}
                                      fontWeight={600}
                                      fill="#334155"
                                    >
                                      {total.toFixed(1)}
                                    </text>
                                  );
                                }}
                              />
                            )}
                          </Bar>
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  );
                })()}
              </Box>
            </Paper>
          </Grid>

          {/* RIGHT */}
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <Paper
              sx={{
                ...chartCard,
                flex: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Select
                fullWidth
                size="small"
                sx={{ mb: 1 }}
                value={country || ""}
                displayEmpty
                onChange={(e) => setCountry(e.target.value)}
                renderValue={(selected) => {
                  if (!selected) return "Select Country";
                  return selected;
                }}
              >
                {selectedDDPCountryList?.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>

              <Typography fontSize={13} mb={1} mt={2} sx={centerDiv}>
                Cost breakdown - Quarter Wise (USD per MT)
              </Typography>

              {/* old: ResponsiveBar from @nivo/bar for Quarter Wise */}
              <Box sx={{ flex: 1, minHeight: { xs: 250, md: 200 } }}>
                {(() => {
                  const quarterSortValue = (label = "") => {
                    const match = String(label).match(/Q(\d)\D*(\d{4})/i);
                    if (!match) return 0;
                    const [, q, year] = match;
                    return Number(year) * 10 + Number(q);
                  };
                  const qwData = Array.isArray(costBreakdownQuarterWise)
                    ? [...costBreakdownQuarterWise].sort(
                      (a, b) =>
                        quarterSortValue(a.quarter) -
                        quarterSortValue(b.quarter),
                    )
                    : [];
                  if (qwData.length === 0) {
                    return (
                      <Box
                        sx={{
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#666",
                          fontSize: 14,
                        }}
                      >
                        No Data Found
                      </Box>
                    );
                  }
                  const qwKeys =
                    qwData.length > 0
                      ? Object.keys(qwData[0]).filter(
                        (key) => key !== "quarter",
                      )
                      : [];
                  const qwColors = [
                    "#1E0143",
                    "#1021AD",
                    "#592DFF",
                    "#000C8E",
                    "#52008E",
                    "#901FB2",
                    "#5C5CC9",
                    "#9F91F3",
                  ];
                  return (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={qwData}
                        margin={{
                          top: 24,
                          right: 20,
                          bottom: isMobile ? 10 : 30,
                          left: isMobile ? 0 : 10,
                        }}
                        barGap={0}
                        barCategoryGap={isMobile ? "15%" : "30%"}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          vertical={false}
                          stroke="#f0f0f0"
                        />
                        <XAxis
                          dataKey="quarter"
                          tickLine={false}
                          tick={{ fontSize: isMobile ? 10 : 12 }}
                        />
                        <YAxis
                          tickLine={false}
                          tick={{ fontSize: isMobile ? 10 : 12 }}
                        />
                        <Tooltip
                          formatter={(v) => Number(v)?.toFixed(1)}
                          contentStyle={{ fontSize: 12, borderRadius: 4 }}
                          wrapperStyle={{ zIndex: 1000, pointerEvents: "none" }}
                          cursor={{ fill: "transparent" }}
                        />
                        <Legend
                          verticalAlign="bottom"
                          align="center"
                          wrapperStyle={{
                            paddingTop: 5,
                            fontSize: isMobile ? 9 : 11,
                            lineHeight: "18px",
                          }}
                          iconType="square"
                          iconSize={10}
                        />
                        {qwKeys.map((key, i) => (
                          <Bar
                            key={key}
                            dataKey={key}
                            stackId="stack"
                            fill={qwColors[i % qwColors.length]}
                          >
                            {i === qwKeys.length - 1 && (
                              <LabelList
                                dataKey={key}
                                position="top"
                                content={({ x, y, width, index }) => {
                                  const row = qwData[index];
                                  const total = qwKeys.reduce(
                                    (sum, k) => sum + (Number(row?.[k]) || 0),
                                    0,
                                  );
                                  return (
                                    <text
                                      x={x + width / 2}
                                      y={y - 6}
                                      textAnchor="middle"
                                      fontSize={11}
                                      fontWeight={600}
                                      fill="#334155"
                                    >
                                      {total.toFixed(1)}
                                    </text>
                                  );
                                }}
                              />
                            )}
                          </Bar>
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  );
                })()}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Cost Breakdown Table */}
        <Paper
          sx={{
            ...chartCard,
            mt: 2,
            borderRadius: 4,
            border: "1px solid #eef0f4",
            boxShadow: "0 4px 20px rgba(15, 23, 42, 0.04)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2.5,
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1.5,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background:
                    "linear-gradient(135deg, #592DFF 0%, #1E0143 100%)",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M4 19V5M4 19H20M4 19L9 12L13 15L20 7"
                    stroke="#fff"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Box>
              <Box>
                <Typography fontSize={15} fontWeight={700} color="#0f172a">
                  Cost breakdown - Quarter Wise (USD per MT)
                </Typography>
                <Typography fontSize={12} color="#94a3b8">
                  Per-quarter cost components across selected countries
                </Typography>
              </Box>
            </Box>

            {tableCountries.length > 1 && (
              <ToggleButtonGroup
                size="small"
                exclusive
                value={tableLayout}
                onChange={(e, value) => {
                  if (value) setTableLayout(value);
                }}
                sx={{
                  "& .MuiToggleButton-root": {
                    textTransform: "none",
                    fontSize: 12.5,
                    fontWeight: 600,
                    px: 1.5,
                    color: "#64748b",
                    borderColor: "#eef0f4",
                    "&.Mui-selected": {
                      bgcolor: "#f1edff",
                      color: "#592DFF",
                      "&:hover": { bgcolor: "#e9e2ff" },
                    },
                  },
                }}
              >
                <ToggleButton value="row">
                  <ViewColumnIcon fontSize="small" sx={{ mr: 0.5 }} />
                  Single Line
                </ToggleButton>
                <ToggleButton value="column">
                  <ViewStreamIcon fontSize="small" sx={{ mr: 0.5 }} />
                  Next Row
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6}>
              <Select
                fullWidth
                size="small"
                multiple
                displayEmpty
                value={tableCountries}
                onChange={(e) => {
                  const value = e.target.value;
                  const next =
                    typeof value === "string" ? value.split(",") : value;
                  // Keep at least one country selected whenever the list isn't empty
                  if (!next.length && selectedDDPCountryList?.length) {
                    return;
                  }
                  setTableCountries(next);
                }}
                renderValue={(selected) => {
                  if (!selected.length) return "Select Country";
                  return selected.join(", ");
                }}
                sx={{
                  borderRadius: 2.5,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#eef0f4",
                  },
                }}
              >
                {selectedDDPCountryList?.map((c) => {
                  const isSelected = tableCountries.includes(c);
                  return (
                    <MenuItem key={c} value={c}>
                      <Checkbox checked={isSelected} />
                      <ListItemText primary={c} />
                    </MenuItem>
                  );
                })}
              </Select>
            </Grid>
          </Grid>

          {!tableCountries.length ? (
            <Box
              sx={{
                py: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#666",
                fontSize: 14,
              }}
            >
              Select a country to view cost breakdown
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection:
                  tableLayout === "row"
                    ? { xs: "column", md: "row" }
                    : "column",
                gap: tableLayout === "row" ? 2 : 3,
              }}
            >
              {tableCountries.map((c) => {
                const data = tableDataByCountry[c] || [];
                const rowOrder = [
                  "EXW",
                  "FOB",
                  "CIF",
                  "Logistics",
                  "Port Charges",
                  "Ocean Freight",
                  "Insurance",
                  "Taxes",
                  "Tariff",
                  "Other Duty",
                ];
                const rowKeys = data.length
                  ? rowOrder.filter((key) =>
                    data.some((row) => row[key] !== undefined),
                  )
                  : [];

                const countryCode = countryToFlagCode[c];
                const totalsByQuarter = data.map((row) =>
                  rowKeys.reduce(
                    (sum, key) => sum + (toNumber(row[key]) || 0),
                    0,
                  ),
                );

                return (
                  <Box
                    key={c}
                    sx={
                      tableLayout === "row"
                        ? {
                          flex:
                            tableCountries.length > 1 ? "1 1 0" : "1 1 auto",
                          minWidth: 0,
                        }
                        : { width: "100%" }
                    }
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                        bgcolor: "#f7f8fc",
                        borderRadius: "999px",
                        pl: countryCode ? 0.5 : 1.25,
                        pr: 1.25,
                        py: 0.5,
                        mb: 1.5,
                      }}
                    >
                      {countryCode && (
                        <Box
                          component="img"
                          src={`https://flagcdn.com/w20/${countryCode.toLowerCase()}.png`}
                          alt=""
                          sx={{ width: 18, height: 18, borderRadius: "50%" }}
                        />
                      )}
                      <Typography fontSize={13} fontWeight={700} color="#1e293b">
                        {c}
                      </Typography>
                    </Box>
                    {!rowKeys.length ? (
                      <Box
                        sx={{
                          py: 4,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#666",
                          fontSize: 14,
                        }}
                      >
                        No Data Found
                      </Box>
                    ) : (
                      <TableContainer
                        sx={{
                          overflowX: "auto",
                          border: "1px solid #eef0f4",
                          borderRadius: 3,
                        }}
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell
                                sx={{
                                  bgcolor: "#f7f8fc",
                                  color: "#94a3b8",
                                  fontSize: 11,
                                  fontWeight: 700,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.06em",
                                  border: "none",
                                  py: 1.5,
                                }}
                              >
                                Cost Component
                              </TableCell>
                              {data.map((row) => (
                                <TableCell
                                  key={row.quarter}
                                  align="right"
                                  sx={{
                                    bgcolor: "#f7f8fc",
                                    color: "#94a3b8",
                                    fontSize: 11,
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.06em",
                                    border: "none",
                                    py: 1.5,
                                  }}
                                >
                                  {row.quarter}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {rowKeys.map((key, rowIndex) => (
                              <TableRow
                                key={key}
                                sx={{
                                  bgcolor:
                                    rowIndex % 2 === 1 ? "#fbfbfd" : "#fff",
                                  "&:hover": { bgcolor: "#f4f2ff" },
                                  transition: "background-color 0.15s ease",
                                }}
                              >
                                <TableCell
                                  sx={{
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: "#1e293b",
                                    borderColor: "#eef0f4",
                                    py: 1.4,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        flexShrink: 0,
                                        bgcolor: getCostComponentColor(key),
                                      }}
                                    />
                                    {key}
                                  </Box>
                                </TableCell>
                                {data.map((row) => (
                                  <TableCell
                                    key={row.quarter}
                                    align="right"
                                    sx={{
                                      fontSize: 13,
                                      fontWeight: 500,
                                      color: "#334155",
                                      borderColor: "#eef0f4",
                                      py: 1.4,
                                      fontVariantNumeric: "tabular-nums",
                                    }}
                                  >
                                    {toNumber(row[key]).toFixed(1)}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                            <TableRow>
                              <TableCell
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 700,
                                  color: "#1e0143",
                                  bgcolor: "#f1edff",
                                  border: "none",
                                  py: 1.5,
                                }}
                              >
                                Total
                              </TableCell>
                              {totalsByQuarter.map((total, i) => (
                                <TableCell
                                  key={data[i].quarter}
                                  align="right"
                                  sx={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: "#1e0143",
                                    bgcolor: "#f1edff",
                                    border: "none",
                                    py: 1.5,
                                    fontVariantNumeric: "tabular-nums",
                                  }}
                                >
                                  {total.toFixed(1)}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>

        {showSpendAnalytics && (
          <>
            {/* Spend Analytics — Supplier Cost Breakdown */}
            <Paper
              sx={{
                mt: 2,
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid #eef0f4",
                boxShadow: "0 4px 20px rgba(15, 23, 42, 0.04)",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  overflow: "hidden",
                  background:
                    "linear-gradient(135deg, #2C003E 0%, #592DFF 100%)",
                  color: "#fff",
                  px: 3,
                  py: 2.5,
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: -40,
                    right: -30,
                    width: 160,
                    height: 160,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.06)",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    bottom: -60,
                    right: 80,
                    width: 120,
                    height: 120,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.04)",
                  }}
                />
                <Typography
                  fontSize={20}
                  fontWeight={700}
                  sx={{
                    position: "relative",
                    letterSpacing: "-0.01em",
                    textAlign: "center",
                  }}
                >
                  Spend Analytics
                </Typography>
              </Box>

              <Box sx={{ p: { xs: 2, md: 3 } }}>
                {/* Filter chips (functional dropdowns, UI-only for this sample data section) */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, 1fr)",
                      md: "repeat(4, 1fr)",
                    },
                    gap: 1,
                    mb: 3,
                  }}
                >
                  {/* Category — searchable, infinite-scroll list */}
                  <Box
                    sx={{
                      flex: "1 1 150px",
                      minWidth: 130,
                      borderRadius: 2.5,
                      background: "#f7f8fc",
                      border: "1px solid #eef0f4",
                      px: 1.5,
                      py: 0.5,
                      transition: "box-shadow 0.15s ease",
                      "&:hover": {
                        boxShadow: "0 4px 12px rgba(15, 23, 42, 0.06)",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        color: "#94a3b8",
                        mt: 0.5,
                      }}
                    >
                      Category
                    </Typography>
                    <Autocomplete
                      options={spendCategoryOptions}
                      // Filtering happens server-side via category_name; disabling
                      // MUI's client-side filter keeps the full appended list
                      // stable across pages instead of being recomputed/reset
                      // every time new options are appended.
                      filterOptions={(x) => x}
                      getOptionLabel={(option) => option?.name || ""}
                      isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                      }
                      value={spendCategory}
                      loading={spendCategoryLoading}
                      onOpen={() => {
                        if (spendCategoryOptions.length === 0) {
                          fetchSpendCategories("", 1);
                        }
                      }}
                      onChange={(e, newValue) => {
                        setSpendCategory(newValue);
                      }}
                      onInputChange={(e, newInputValue, reason) => {
                        if (reason === "input") {
                          setSpendCategorySearch(newInputValue);
                        }
                      }}
                      ListboxProps={{
                        onScroll: (event) => {
                          const el = event.currentTarget;
                          const atBottom =
                            el.scrollTop + el.clientHeight >= el.scrollHeight - 10;
                          if (atBottom) {
                            spendCategoryScrollBottomRef.current();
                          }
                        },
                        style: { maxHeight: 220, overflowY: "auto" },
                      }}
                      PaperComponent={SpendCategoryPaper}
                      disableClearable
                      size="small"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          placeholder="Select Category"
                          InputProps={{
                            ...params.InputProps,
                            disableUnderline: true,
                            endAdornment: (
                              <>
                                {spendCategoryLoading ? (
                                  <CircularProgress size={12} sx={{ mr: 0.5 }} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                          sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#1e293b",
                            "& .MuiInputBase-input": {
                              py: 0.25,
                              fontSize: 13,
                              fontWeight: 600,
                            },
                          }}
                        />
                      )}
                    />
                  </Box>

                  {/* Material — cascades off the selected Category's id */}
                  <Box
                    sx={{
                      flex: "1 1 150px",
                      minWidth: 130,
                      borderRadius: 2.5,
                      background: "#f7f8fc",
                      border: "1px solid #eef0f4",
                      px: 1.5,
                      py: 0.5,
                      opacity: spendCategory ? 1 : 0.5,
                      transition: "box-shadow 0.15s ease",
                      "&:hover": {
                        boxShadow: spendCategory
                          ? "0 4px 12px rgba(15, 23, 42, 0.06)"
                          : "none",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        color: "#94a3b8",
                        mt: 0.5,
                      }}
                    >
                      Material
                    </Typography>
                    <Autocomplete
                      options={spendMaterialOptions}
                      disabled={!spendCategory}
                      filterOptions={(x) => x}
                      getOptionLabel={(option) => option?.material_name || ""}
                      isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                      }
                      value={spendMaterialSel}
                      loading={spendMaterialLoading}
                      onOpen={() => {
                        if (spendMaterialOptions.length === 0) {
                          fetchSpendMaterials(spendCategory?.id, "", 1);
                        }
                      }}
                      onChange={(e, newValue) => {
                        setSpendMaterialSel(newValue);
                      }}
                      onInputChange={(e, newInputValue, reason) => {
                        if (reason === "input") {
                          setSpendMaterialSearch(newInputValue);
                        }
                      }}
                      ListboxProps={{
                        onScroll: (event) => {
                          const el = event.currentTarget;
                          const atBottom =
                            el.scrollTop + el.clientHeight >= el.scrollHeight - 10;
                          if (atBottom) {
                            spendMaterialScrollBottomRef.current();
                          }
                        },
                        style: { maxHeight: 220, overflowY: "auto" },
                      }}
                      PaperComponent={SpendMaterialPaper}
                      disableClearable
                      size="small"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          placeholder={
                            spendCategory
                              ? "Select Material"
                              : "Select Category first"
                          }
                          InputProps={{
                            ...params.InputProps,
                            disableUnderline: true,
                            endAdornment: (
                              <>
                                {spendMaterialLoading ? (
                                  <CircularProgress size={12} sx={{ mr: 0.5 }} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                          sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#1e293b",
                            "& .MuiInputBase-input": {
                              py: 0.25,
                              fontSize: 13,
                              fontWeight: 600,
                            },
                          }}
                        />
                      )}
                    />
                  </Box>

                  {/* HS Code — derived from the selected Material, read-only */}
                  <Box
                    sx={{
                      flex: "1 1 150px",
                      minWidth: 130,
                      borderRadius: 2.5,
                      background: "#f7f8fc",
                      border: "1px solid #eef0f4",
                      px: 1.5,
                      py: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        color: "#94a3b8",
                      }}
                    >
                      HS Code
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#1e293b",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={
                        spendMaterialSel?.hs_code_short_description
                          ? `${spendMaterialSel.hsncode} — ${spendMaterialSel.hs_code_short_description}`
                          : undefined
                      }
                    >
                      {spendMaterialSel?.hsncode || "—"}
                    </Typography>
                  </Box>

                  {/* Destination — cascades off the selected Material's id */}
                  <Box
                    sx={{
                      flex: "1 1 150px",
                      minWidth: 130,
                      borderRadius: 2.5,
                      background: "#f7f8fc",
                      border: "1px solid #eef0f4",
                      px: 1.5,
                      py: 0.5,
                      opacity: spendMaterialSel ? 1 : 0.5,
                      transition: "box-shadow 0.15s ease",
                      "&:hover": {
                        boxShadow: spendMaterialSel
                          ? "0 4px 12px rgba(15, 23, 42, 0.06)"
                          : "none",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        color: "#94a3b8",
                        mt: 0.5,
                      }}
                    >
                      Destination
                    </Typography>
                    <Select
                      fullWidth
                      variant="standard"
                      disableUnderline
                      displayEmpty
                      disabled={!spendMaterialSel}
                      value={spendDestination}
                      onChange={(e) => setSpendDestination(e.target.value)}
                      endAdornment={
                        spendDestinationLoading ? (
                          <CircularProgress size={12} sx={{ mr: 2 }} />
                        ) : null
                      }
                      renderValue={(selected) =>
                        selected ||
                        (spendMaterialSel
                          ? "Select Destination"
                          : "Select Material first")
                      }
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#1e293b",
                        "& .MuiSelect-select": {
                          py: 0.25,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        },
                      }}
                    >
                      {spendDestinationOptions.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>

                  {/* Suppliers — cascades off Material + Destination */}
                  <Box
                    sx={{
                      flex: "1 1 150px",
                      minWidth: 130,
                      borderRadius: 2.5,
                      background: "#f7f8fc",
                      border: "1px solid #eef0f4",
                      px: 1.5,
                      py: 0.5,
                      opacity: spendDestination ? 1 : 0.5,
                      transition: "box-shadow 0.15s ease",
                      "&:hover": {
                        boxShadow: spendDestination
                          ? "0 4px 12px rgba(15, 23, 42, 0.06)"
                          : "none",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        color: "#94a3b8",
                        mt: 0.5,
                      }}
                    >
                      Suppliers
                    </Typography>
                    <Select
                      fullWidth
                      multiple
                      variant="standard"
                      disableUnderline
                      displayEmpty
                      disabled={!spendDestination}
                      value={spendSuppliers}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.includes("__select_all__")) {
                          setSpendSuppliers(
                            spendSuppliers.length === spendSupplierOptions.length
                              ? []
                              : spendSupplierOptions,
                          );
                          return;
                        }
                        setSpendSuppliers(value);
                      }}
                      endAdornment={
                        spendSupplierLoading ? (
                          <CircularProgress size={12} sx={{ mr: 2 }} />
                        ) : null
                      }
                      renderValue={(selected) =>
                        selected.length
                          ? selected.length === spendSupplierOptions.length
                            ? "All selected"
                            : selected.join(", ")
                          : spendDestination
                            ? "Select Suppliers"
                            : "Select Destination first"
                      }
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#1e293b",
                        "& .MuiSelect-select": {
                          py: 0.25,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        },
                      }}
                    >
                      {spendSupplierOptions.length > 0 && (
                        <MenuItem value="__select_all__">
                          <Checkbox
                            size="small"
                            checked={
                              spendSuppliers.length ===
                              spendSupplierOptions.length
                            }
                            indeterminate={
                              spendSuppliers.length > 0 &&
                              spendSuppliers.length < spendSupplierOptions.length
                            }
                          />
                          <ListItemText
                            primary="Select All"
                            primaryTypographyProps={{ fontWeight: 700 }}
                          />
                        </MenuItem>
                      )}
                      {spendSupplierOptions.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          <Checkbox
                            size="small"
                            checked={spendSuppliers.includes(opt)}
                          />
                          <ListItemText primary={opt} />
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>

                  {/* Supplier Location — cascades off Material + Destination + Supplier */}
                  <Box
                    sx={{
                      flex: "1 1 150px",
                      minWidth: 130,
                      borderRadius: 2.5,
                      background: "#f7f8fc",
                      border: "1px solid #eef0f4",
                      px: 1.5,
                      py: 0.5,
                      opacity: spendSuppliers.length ? 1 : 0.5,
                      transition: "box-shadow 0.15s ease",
                      "&:hover": {
                        boxShadow: spendSuppliers.length
                          ? "0 4px 12px rgba(15, 23, 42, 0.06)"
                          : "none",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        color: "#94a3b8",
                        mt: 0.5,
                      }}
                    >
                      Supplier Location
                    </Typography>
                    <Select
                      fullWidth
                      multiple
                      variant="standard"
                      disableUnderline
                      displayEmpty
                      disabled={!spendSuppliers.length}
                      value={spendSupplierLocations}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.includes("__select_all__")) {
                          setSpendSupplierLocations(
                            spendSupplierLocations.length ===
                              spendSupplierLocationOptions.length
                              ? []
                              : spendSupplierLocationOptions,
                          );
                          return;
                        }
                        setSpendSupplierLocations(value);
                      }}
                      endAdornment={
                        spendSupplierLocationLoading ? (
                          <CircularProgress size={12} sx={{ mr: 2 }} />
                        ) : null
                      }
                      renderValue={(selected) =>
                        selected.length
                          ? selected.length === spendSupplierLocationOptions.length
                            ? "All selected"
                            : selected.join(", ")
                          : spendSuppliers.length
                            ? "Select Locations"
                            : "Select Supplier first"
                      }
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#1e293b",
                        "& .MuiSelect-select": {
                          py: 0.25,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        },
                      }}
                    >
                      {spendSupplierLocationOptions.length > 0 && (
                        <MenuItem value="__select_all__">
                          <Checkbox
                            size="small"
                            checked={
                              spendSupplierLocations.length ===
                              spendSupplierLocationOptions.length
                            }
                            indeterminate={
                              spendSupplierLocations.length > 0 &&
                              spendSupplierLocations.length <
                                spendSupplierLocationOptions.length
                            }
                          />
                          <ListItemText
                            primary="Select All"
                            primaryTypographyProps={{ fontWeight: 700 }}
                          />
                        </MenuItem>
                      )}
                      {spendSupplierLocationOptions.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          <Checkbox
                            size="small"
                            checked={spendSupplierLocations.includes(opt)}
                          />
                          <ListItemText primary={opt} />
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>

                  {/* Period — from the master period list, defaults to is_current */}
                  <Box
                    sx={{
                      flex: "1 1 150px",
                      minWidth: 130,
                      borderRadius: 2.5,
                      background: "#f7f8fc",
                      border: "1px solid #eef0f4",
                      px: 1.5,
                      py: 0.5,
                      transition: "box-shadow 0.15s ease",
                      "&:hover": {
                        boxShadow: "0 4px 12px rgba(15, 23, 42, 0.06)",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        color: "#94a3b8",
                        mt: 0.5,
                      }}
                    >
                      Period
                    </Typography>
                    <Select
                      fullWidth
                      variant="standard"
                      disableUnderline
                      displayEmpty
                      value={spendPeriod ?? ""}
                      onChange={(e) => setSpendPeriod(e.target.value)}
                      endAdornment={
                        spendPeriodLoading ? (
                          <CircularProgress size={12} sx={{ mr: 2 }} />
                        ) : null
                      }
                      renderValue={(selected) =>
                        spendPeriodOptions.find((p) => p.id === selected)
                          ?.period_name || "Select Period"
                      }
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#1e293b",
                        "& .MuiSelect-select": {
                          py: 0.25,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        },
                      }}
                    >
                      {spendPeriodOptions.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.period_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>

                  {/* Clear all Spend Analytics filters */}
                  <Button
                    onClick={handleClearSpendFilters}
                    startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      flex: "1 1 150px",
                      minWidth: 130,
                      borderRadius: 2.5,
                      border: "1px solid #eef0f4",
                      background: "#f7f8fc",
                      color: "#592DFF",
                      fontSize: 13,
                      fontWeight: 600,
                      textTransform: "none",
                      px: 1.5,
                      "&:hover": {
                        background: "#f1edff",
                        borderColor: "#e2d9ff",
                        boxShadow: "0 4px 12px rgba(15, 23, 42, 0.06)",
                      },
                    }}
                  >
                    Clear Filters
                  </Button>
                </Box>

                {/* Section title */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1.5,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#fff",
                      background:
                        "linear-gradient(135deg, #592DFF 0%, #1E0143 100%)",
                    }}
                  >
                    1
                  </Box>
                  <Typography fontSize={16} fontWeight={700} color="#0f172a">
                    Supplier Cost Breakdown
                  </Typography>
                </Box>
                {/* <Typography fontSize={12.5} color="#94a3b8" mb={2}>
                  Quoted price and buyer borne cost components for the selected
                  suppliers
                </Typography> */}

                {supplierCostLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1.5,
                      py: 6,
                    }}
                  >
                    <CircularProgress size={20} />
                    <Typography fontSize={13} color="#94a3b8">
                      Loading supplier cost breakdown…
                    </Typography>
                  </Box>
                ) : supplierCostData.length === 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      py: 6,
                    }}
                  >
                    <Typography fontSize={13} color="#94a3b8">
                      Select Material, Destination, Suppliers and Supplier
                      Location to view the cost breakdown
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {/* Quoted vs EXW chart */}
                    <Typography
                      fontSize={13}
                      fontWeight={600}
                      mb={1}
                      sx={{ display: "flex", justifyContent: "center" }}
                    >
                      Quoted vs EXW Price
                    </Typography>
                    <Box sx={{ height: { xs: 260, sm: 300 } }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                          data={supplierCostData}
                          margin={{
                            top: 20,
                            right: isMobile ? 10 : 30,
                            bottom: 20,
                            left: isMobile ? 10 : 20,
                          }}
                        >
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis
                            dataKey="supplier"
                            tick={{ fontSize: isMobile ? 10 : 12 }}
                            tickLine={false}
                          />
                          <YAxis
                            tick={{ fontSize: isMobile ? 10 : 12 }}
                            tickLine={false}
                            tickFormatter={(v) => `$${v.toLocaleString()}`}
                          />
                          <Tooltip
                            formatter={(v) => `$${Number(v).toLocaleString()}`}
                            contentStyle={{ fontSize: 12, borderRadius: 4 }}
                          />
                          <Legend
                            verticalAlign="bottom"
                            align="center"
                            wrapperStyle={{ paddingTop: 10, fontSize: 12 }}
                          />
                          <Bar
                            dataKey="quotedPrice"
                            name="Quoted Price"
                            fill="#592DFF"
                            radius={[6, 6, 0, 0]}
                            barSize={isMobile ? 20 : 36}
                          >
                            <LabelList
                              dataKey="quotedPrice"
                              position="top"
                              formatter={(v) => `$${v.toLocaleString()}`}
                              fontSize={11}
                              fontWeight={600}
                              fill="#334155"
                            />
                          </Bar>
                          <Line
                            type="monotone"
                            dataKey="exw"
                            name="EXW"
                            stroke="#00C2A8"
                            strokeWidth={2.5}
                            dot={{ r: 4, fill: "#fff", stroke: "#00C2A8", strokeWidth: 2 }}
                            activeDot={{ r: 6 }}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Box>

                    {/* Supplier table */}
                    <TableContainer
                      sx={{
                        mt: 2,
                        maxHeight: 420,
                        overflow: "auto",
                        border: "1px solid #eef0f4",
                        borderRadius: 2.5,
                        boxShadow: "0 2px 10px rgba(15, 23, 42, 0.03)",
                      }}
                    >
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            {[
                              "Supplier",
                              "Supplier Location",
                              "Volume (t)",
                              "Current Spend ($)",
                              "Quoted Incoterm",
                              "Quoted ($/t)",
                              "Taxes",
                              "Other Duties",
                              "Tariff",
                              "Logistics",
                              "Port Charges",
                              "Insurance",
                              "Ocean Freight",
                              "EXW",
                            ].map((h, i, arr) => (
                              <TableCell
                                key={h}
                                align={h === "Supplier" ? "left" : "center"}
                                sx={{
                                  position: "sticky",
                                  top: 0,
                                  ...(i === 0 ? { left: 0 } : {}),
                                  ...(i === arr.length - 1 ? { right: 0 } : {}),
                                  zIndex: i === 0 || i === arr.length - 1 ? 3 : 2,
                                  bgcolor: "#f7f8fc",
                                  color: "#64748b",
                                  fontSize: 10.5,
                                  fontWeight: 700,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.05em",
                                  whiteSpace: "nowrap",
                                  py: 1.4,
                                  borderBottom: "1px solid #eef0f4",
                                  borderLeft:
                                    i === arr.length - 1
                                      ? "1px solid #eef0f4"
                                      : "none",
                                  borderRight:
                                    i === 0 || i === 3 || i === 5
                                      ? "1px solid #eef0f4"
                                      : "none",
                                }}
                              >
                                {h}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {supplierCostData.map((row, rowIndex) => (
                            <TableRow
                              key={row.supplier}
                              sx={{
                                bgcolor: rowIndex % 2 === 1 ? "#fbfbfd" : "#fff",
                                "&:hover": { bgcolor: "#f4f2ff" },
                                transition: "background-color 0.15s ease",
                                "& td": { py: 1.1 },
                              }}
                            >
                              <TableCell
                                sx={{
                                  position: "sticky",
                                  left: 0,
                                  zIndex: 1,
                                  bgcolor: rowIndex % 2 === 1 ? "#fbfbfd" : "#fff",
                                  fontWeight: row.bold ? 700 : 600,
                                  fontSize: 13,
                                  color: "#1e293b",
                                  borderColor: "#eef0f4",
                                  borderLeft: "3px solid transparent",
                                  "tr:hover &": {
                                    bgcolor: "#f4f2ff",
                                    borderLeftColor: "#592DFF",
                                  },
                                }}
                              >
                                {row.supplier}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ fontSize: 13, color: "#475569", borderColor: "#eef0f4" }}
                              >
                                {row.location}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: 13, borderColor: "#eef0f4" }}>
                                {fmt2(row.volume)}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: "#334155",
                                  borderColor: "#eef0f4",
                                }}
                              >
                                ${fmt2(row.currentSpend)}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ fontSize: 13, color: "#475569", borderColor: "#eef0f4" }}
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: "#592DFF",
                                    bgcolor: "#f1edff",
                                    borderRadius: 1,
                                    px: 0.8,
                                    py: 0.2,
                                  }}
                                >
                                  {row.incoterm}
                                </Box>
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: "#334155",
                                  borderColor: "#eef0f4",
                                }}
                              >
                                ${fmt2(row.quotedPrice)}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ fontSize: 13, color: "#94a3b8", borderColor: "#eef0f4" }}
                              >
                                {fmt2(row.taxes)}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ fontSize: 13, color: "#94a3b8", borderColor: "#eef0f4" }}
                              >
                                {fmt2(row.otherDuties)}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ fontSize: 13, color: "#94a3b8", borderColor: "#eef0f4" }}
                              >
                                {fmt2(row.tariff)}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ fontSize: 13, color: "#94a3b8", borderColor: "#eef0f4" }}
                              >
                                {fmt2(row.logistics)}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ fontSize: 13, color: "#94a3b8", borderColor: "#eef0f4" }}
                              >
                                {fmt2(row.portCharges)}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ fontSize: 13, color: "#94a3b8", borderColor: "#eef0f4" }}
                              >
                                {fmt2(row.insurance)}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ fontSize: 13, color: "#94a3b8", borderColor: "#eef0f4" }}
                              >
                                {fmt2(row.oceanFreight)}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{
                                  position: "sticky",
                                  right: 0,
                                  zIndex: 1,
                                  fontSize: 13,
                                  fontWeight: 700,
                                  color: "#1e0143",
                                  borderColor: "#eef0f4",
                                  borderLeft: "1px solid #eef0f4",
                                  bgcolor: "#faf9ff",
                                }}
                              >
                                ${fmt2(row.exw)}
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow>
                            <TableCell
                              sx={{
                                position: "sticky",
                                left: 0,
                                zIndex: 1,
                                fontWeight: 700,
                                fontSize: 13,
                                color: "#1e0143",
                                bgcolor: "#f1edff",
                                borderTop: "2px solid #e2d9ff",
                                borderBottom: "none",
                              }}
                            >
                              Total
                            </TableCell>
                            <TableCell
                              sx={{
                                bgcolor: "#f1edff",
                                borderTop: "2px solid #e2d9ff",
                                borderBottom: "none",
                              }}
                            />
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: 700,
                                fontSize: 13,
                                color: "#1e0143",
                                bgcolor: "#f1edff",
                                borderTop: "2px solid #e2d9ff",
                                borderBottom: "none",
                              }}
                            >
                              {fmt2(
                                supplierCostData.reduce(
                                  (sum, r) => sum + r.volume,
                                  0,
                                ),
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: 700,
                                fontSize: 13,
                                color: "#1e0143",
                                bgcolor: "#f1edff",
                                borderTop: "2px solid #e2d9ff",
                                borderBottom: "none",
                              }}
                            >
                              $
                              {fmt2(
                                supplierCostData.reduce(
                                  (sum, r) => sum + r.currentSpend,
                                  0,
                                ),
                              )}
                            </TableCell>
                            <TableCell
                              colSpan={9}
                              sx={{
                                bgcolor: "#f1edff",
                                borderTop: "2px solid #e2d9ff",
                                borderBottom: "none",
                              }}
                            />
                            <TableCell
                              sx={{
                                position: "sticky",
                                right: 0,
                                zIndex: 1,
                                bgcolor: "#f1edff",
                                borderTop: "2px solid #e2d9ff",
                                borderBottom: "none",
                                borderLeft: "1px solid #e2d9ff",
                              }}
                            />
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Box>
            </Paper>

            {/* Volume Optimizer */}
            <Paper
              sx={{
                mt: 2,
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: "1px solid #eef0f4",
                boxShadow: "0 4px 20px rgba(15, 23, 42, 0.04)",
              }}
            >
              {supplierCostLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    py: 6,
                  }}
                >
                  <CircularProgress size={20} />
                  <Typography fontSize={13} color="#94a3b8">
                    Loading volume optimizer…
                  </Typography>
                </Box>
              ) : volumeOptimizerData.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 6,
                  }}
                >
                  <Typography fontSize={13} color="#94a3b8">
                    Select Material, Destination, Suppliers and Supplier Location
                    to view volume optimization
                  </Typography>
                </Box>
              ) : (
                (() => {
                  // Always derive totals from the live rows — not the
                  // frozen API `total` object — so editing New Allocation %
                  // (which updates newAlloc/newSpend/savings per row) moves
                  // every total/metric card immediately instead of staying
                  // pinned to whatever the API returned on load.
                  const totalVolume = volumeOptimizerData.reduce(
                    (sum, r) => sum + r.volume,
                    0,
                  );
                  const currentSpendTotal = volumeOptimizerData.reduce(
                    (sum, r) => sum + r.currentSpend,
                    0,
                  );
                  const ddpSpendTotal = volumeOptimizerData.reduce(
                    (sum, r) => sum + r.ddpSpend,
                    0,
                  );
                  const newSpendTotal = volumeOptimizerData.reduce(
                    (sum, r) => sum + r.newSpend,
                    0,
                  );
                  const currentAllocPctTotal = volumeOptimizerData.reduce(
                    (sum, r) => sum + (Number(r.currentAlloc) || 0),
                    0,
                  );
                  const newAllocPctTotal = volumeOptimizerData.reduce(
                    (sum, r) => sum + (Number(r.newAlloc) || 0),
                    0,
                  );
                  const netSaving = ddpSpendTotal - newSpendTotal;
                  const savingsPct = (netSaving / ddpSpendTotal) * 100;
                  const fmtMoney = (v) =>
                    `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                  const fmtMillions = (v) => `$${(v / 1_000_000).toFixed(2)} M`;

                  return (
                    <>
                      {/* Section title */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          mb: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 1.5,
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 13,
                            fontWeight: 700,
                            color: "#fff",
                            background:
                              "linear-gradient(135deg, #592DFF 0%, #1E0143 100%)",
                          }}
                        >
                          2
                        </Box>
                        <Typography fontSize={16} fontWeight={700} color="#0f172a">
                          Volume Optimizer
                        </Typography>
                      </Box>
                      {/* <Typography fontSize={12.5} color="#94a3b8" mb={2}>
                        Re-allocating the same {totalVolume.toLocaleString()} t
                        across suppliers to lower total landed spend
                      </Typography> */}

                      {/* Metric cards */}
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 2,
                          mb: 3.5,
                        }}
                      >
                        {[
                          {
                            label: "Current Landed Spend",
                            sub: "at current allocation",
                            value: fmtMillions(ddpSpendTotal),
                          },
                          {
                            label: "Optimized Spend",
                            sub: "at new allocation",
                            value: fmtMillions(newSpendTotal),
                          },
                          {
                            label: "Net Savings",
                            sub: `${savingsPct.toFixed(1)}% of landed spend`,
                            value: fmtMoney(netSaving),
                          },
                        ].map((card) => (
                          <Box
                            key={card.label}
                            sx={{
                              flex: "1 1 180px",
                              minWidth: 170,
                              borderLeft: "4px solid #592DFF",
                              bgcolor: "#f7f8fc",
                              borderRadius: 2,
                              border: "1px solid #eef0f4",
                              borderLeftWidth: "4px",
                              p: 2,
                              transition:
                                "box-shadow 0.15s ease, transform 0.15s ease",
                              "&:hover": {
                                boxShadow: "0 6px 16px rgba(15, 23, 42, 0.08)",
                                transform: "translateY(-1px)",
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 10.5,
                                fontWeight: 700,
                                color: "#94a3b8",
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                              }}
                            >
                              {card.label}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 22,
                                fontWeight: 700,
                                color: "#1e0143",
                                mt: 0.5,
                              }}
                            >
                              {card.value}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                              {card.sub}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          fontSize={12.5}
                          fontWeight={600}
                          color="#475569"
                          mb={1.5}
                        >
                          Volume Allocation        </Typography>

                        {/* Stacked bars */}
                        {[
                          { label: "Current", key: "currentAlloc" },
                          { label: "Optimized", key: "newAlloc" },
                        ].map((row) => (
                          <Box
                            key={row.key}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              mb: 1.5,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#475569",
                                width: 72,
                                flexShrink: 0,
                              }}
                            >
                              {row.label}
                            </Typography>
                            <Box
                              sx={{
                                flex: 1,
                                display: "flex",
                                borderRadius: 1,
                                overflow: "hidden",
                                height: 30,
                              }}
                            >
                              {volumeOptimizerData.map((supplier, i) => (
                                <MuiTooltip
                                  key={supplier.supplier}
                                  title={`${supplier.supplier}: ${supplier[row.key].toFixed(1)}%`}
                                >
                                  <Box
                                    sx={{
                                      width: `${supplier[row.key]}%`,
                                      bgcolor:
                                        VOLUME_OPTIMIZER_COLORS[
                                        i % VOLUME_OPTIMIZER_COLORS.length
                                        ],
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: "#fff",
                                      fontSize: 10,
                                      fontWeight: 600,
                                      cursor: "default",
                                    }}
                                  >
                                    {supplier[row.key].toFixed(1)}%
                                  </Box>
                                </MuiTooltip>
                              ))}
                            </Box>
                          </Box>
                        ))}

                        {/* Axis ticks */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            pl: "84px",
                            mb: 1.5,
                          }}
                        >
                          {["0%", "25%", "50%", "75%", "100%"].map((t) => (
                            <Typography
                              key={t}
                              sx={{ fontSize: 10, color: "#94a3b8" }}
                            >
                              {t}
                            </Typography>
                          ))}
                        </Box>

                        {/* Legend */}
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1.5,
                            mb: 2.5,
                          }}
                        >
                          {volumeOptimizerData.map((supplier, i) => (
                            <Box
                              key={supplier.supplier}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: "2px",
                                  bgcolor:
                                    VOLUME_OPTIMIZER_COLORS[
                                    i % VOLUME_OPTIMIZER_COLORS.length
                                    ],
                                }}
                              />
                              <Typography sx={{ fontSize: 11, color: "#475569" }}>
                                {supplier.supplier}
                              </Typography>
                            </Box>
                          ))}
                        </Box>

                        {/* Table */}
                        <TableContainer
                          sx={{
                            overflowX: "auto",
                            border: "1px solid #eef0f4",
                            borderRadius: 2,
                          }}
                        >
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                {[
                                  { label: "Supplier" },
                                  { label: "Supplier location" },
                                  { label: "Quoted Incoterm" },
                                  { label: "Quoted Price", unit: "($/t)" },
                                  { label: "DDP Price", unit: "($/t)" },
                                  { label: "Volume", unit: "(t)" },
                                  { label: "Current Allocation", unit: "%" },
                                  { label: "New Allocation", unit: "%" },
                                  {
                                    label: "Current Spend by Incoterm",
                                    unit: "($)",
                                  },
                                  { label: "Spend (DDP basis)", unit: "($)" },
                                  {
                                    label: "New Spend (DDP basis)",
                                    unit: "($)",
                                  },
                                  { label: "Savings", unit: "($)" },
                                ].map((h) => (
                                  <TableCell
                                    key={h.label}
                                    align={
                                      h.label === "Supplier" ? "left" : "center"
                                    }
                                    sx={{
                                      bgcolor: "#1E0143",
                                      color: "#fff",
                                      fontSize: 11,
                                      fontWeight: 700,
                                      whiteSpace: "nowrap",
                                      verticalAlign: "top",
                                      py: 1.2,
                                    }}
                                  >
                                    {h.label}
                                    {h.unit && (
                                      <Box
                                        component="span"
                                        sx={{
                                          display: "block",
                                          fontWeight: 400,
                                          fontSize: 10,
                                          color: "rgba(255,255,255,0.65)",
                                          mt: 0.3,
                                        }}
                                      >
                                        {h.unit}
                                      </Box>
                                    )}
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {volumeOptimizerData.map((row) => (
                                <TableRow
                                  key={row.supplier}
                                  sx={{ "&:hover": { bgcolor: "#f8f7fc" } }}
                                >
                                  <TableCell
                                    sx={{
                                      fontWeight: row.bold ? 700 : 600,
                                      fontSize: 12.5,
                                      color: "#1e293b",
                                    }}
                                  >
                                    {row.supplier}
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontSize: 12.5 }}>
                                    {row.location}
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontSize: 12.5 }}>
                                    {row.incoterm}
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontSize: 12.5 }}>
                                    ${fmt2(row.quotedPrice)}
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontSize: 12.5 }}>
                                    ${fmt2(row.ddpPrice)}
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontSize: 12.5 }}>
                                    {fmt2(row.volume)}
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontSize: 12.5 }}>
                                    {row.currentAlloc.toFixed(1)}%
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontSize: 12.5 }}>
                                    <Box
                                      sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      {editingAllocSupplier ===
                                      row.supplier ? (
                                        <TextField
                                          autoFocus
                                          variant="standard"
                                          size="small"
                                          type="number"
                                          value={row.newAlloc}
                                          onChange={(e) =>
                                            handleNewAllocChange(
                                              row.supplier,
                                              e.target.value,
                                            )
                                          }
                                          onBlur={handleNewAllocBlur}
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                              e.target.blur();
                                            }
                                          }}
                                          InputProps={{
                                            disableUnderline: true,
                                            endAdornment: "%",
                                          }}
                                          inputProps={{
                                            style: {
                                              textAlign: "center",
                                              fontSize: 12.5,
                                              padding: 0,
                                            },
                                            step: 0.1,
                                          }}
                                          sx={{
                                            width: 56,
                                            "& input": { textAlign: "center" },
                                          }}
                                        />
                                      ) : (
                                        <>
                                          <span>
                                            {row.newAlloc.toFixed(1)}%
                                          </span>
                                          <EditIcon
                                            onClick={() =>
                                              setEditingAllocSupplier(
                                                row.supplier,
                                              )
                                            }
                                            sx={{
                                              fontSize: 14,
                                              color: "#94a3b8",
                                              cursor: "pointer",
                                              "&:hover": { color: "#592DFF" },
                                            }}
                                          />
                                        </>
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontSize: 12.5 }}>
                                    {fmtMoney(row.currentSpend)}
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontSize: 12.5 }}>
                                    {fmtMoney(row.ddpSpend)}
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontSize: 12.5 }}>
                                    {fmtMoney(row.newSpend)}
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{
                                      fontSize: 12.5,
                                      fontWeight: 700,
                                      color:
                                        row.savings >= 0 ? "#008a3c" : "#d32f2f",
                                    }}
                                  >
                                    {row.savings >= 0 ? "" : "-"}$
                                    {fmt2(Math.abs(row.savings))}
                                  </TableCell>
                                </TableRow>
                              ))}
                              <TableRow>
                                <TableCell
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: 12.5,
                                    bgcolor: "#f1edff",
                                  }}
                                >
                                  TOTAL
                                </TableCell>
                                <TableCell sx={{ bgcolor: "#f1edff" }} />
                                <TableCell sx={{ bgcolor: "#f1edff" }} />
                                <TableCell sx={{ bgcolor: "#f1edff" }} />
                                <TableCell sx={{ bgcolor: "#f1edff" }} />
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: 12.5,
                                    bgcolor: "#f1edff",
                                  }}
                                >
                                  {fmt2(totalVolume)}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: 12.5,
                                    bgcolor: "#f1edff",
                                  }}
                                >
                                  {currentAllocPctTotal.toFixed(1)}%
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: 12.5,
                                    bgcolor: "#f1edff",
                                  }}
                                >
                                  {newAllocPctTotal.toFixed(1)}%
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: 12.5,
                                    bgcolor: "#f1edff",
                                  }}
                                >
                                  {fmtMoney(currentSpendTotal)}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: 12.5,
                                    bgcolor: "#f1edff",
                                  }}
                                >
                                  {fmtMoney(ddpSpendTotal)}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: 12.5,
                                    bgcolor: "#f1edff",
                                  }}
                                >
                                  {fmtMoney(newSpendTotal)}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: 12.5,
                                    bgcolor: "#f1edff",
                                    color: netSaving >= 0 ? "#008a3c" : "#d32f2f",
                                  }}
                                >
                                  {fmtMoney(netSaving)}
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>

                        {/* <Typography
                          sx={{ fontSize: 10.5, color: "#94a3b8", mt: 1.5 }}
                        >
                          Logic: suppliers ranked by volume (descending) — top
                          70% of suppliers (by count) share 70% of allocation
                          equally, remaining share 30% equally. Savings =
                          Current − New Spend; green = saving, red = added cost.
                        </Typography> */}
                      </Box>
                    </>
                  );
                })()
              )}
            </Paper>
          </>
        )}
      </Paper>
    </Box>
  );
}

/* -------------------- STYLES -------------------- */
const infoCard = {
  p: 1,
  borderRadius: 2,
  // background: "#f4ecff",
  background: "#fff",
  color: "#1e0143",
  display: "flex",
  border: "1px solid #1e0143 !important",
  justifyContent: "center",
};
const cardHeader = {
  display: "flex",
  justifyContent: "center",
  width: "100%",
  fontSize: "14px",
  fontWeight: 700,
  color: "#fff",
  background: "#1e0143",
  padding: "10px",
  borderRadius: "8px",
};

const chartCard = {
  p: 2,
  borderRadius: 2,
};

const centerDiv = { display: "center", justifyContent: "center" };
